import { useState } from 'react';
import { useForm } from 'react-hook-form';
import z from 'zod';
import { newProductSchema } from '../../../schemas/newProductSchema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import { addDiscountToProduct } from '../addDiscountToProduct';
import { createNewProduct } from '../createNewProduct';
import { useDialog } from '../../../context/Dialog';
import useIonicToast from '../../../hooks/useIonicToast';
import { addProductImage } from '../addProductImages';
import { updateProduct } from '../updateProduct';

export const useProductCreationService = () => {
  const [step, setStep] = useState<number>(0);
  const handleNext = () => setStep(step + 1);
  const { setBusy, closeDialog } = useDialog();
  const { presentIonToast } = useIonicToast();
  const query = useQueryClient();
  const store_id = useSelector(
    (state: RootState) => state.userStores.activeStore?.id
  );
  const { signedAccessToken } = useSelector(
    (state: RootState) => state.userSession
  );

  const invalidateProductList = async () => {
    query.invalidateQueries({
      queryKey: ['products-catalog'],
      exact: false,
    });
    query.invalidateQueries({
      queryKey: ['products-inventory'],
      exact: false,
    });
  };

  const methods = useForm<z.infer<typeof newProductSchema>>({
    resolver: zodResolver(newProductSchema),
    mode: 'all',
  });
  const validateProductIfo = () =>
    methods.trigger([
      'name',
      'category',
      'hash_tag',
      'price',
      'quantity',
      'description',
      'discount_amount',
      'discount_starts_at',
      'discount_ends_at',
    ]);

  // Clear draft on successful submission
  const handleSubmission = methods.handleSubmit(() => {
    console.log('Product created');
  });

  //HANDLE PRODUCT CREATION
  const createProduct = useMutation({
    mutationFn: async () => {
      if (!signedAccessToken || !store_id) return;
      try {
        setBusy(true);
        const {
          category,
          description,
          hash_tag,
          name,
          price,
          quantity,
          isDiscounted,
          discount_amount,
          discount_ends_at,
          discount_starts_at,
        } = methods.getValues();

        const res = await createNewProduct(
          {
            save_as: 'draft',
            category: category.id,
            description,
            hash_tag: hash_tag.map(({ id }) => id),
            name,
            price,
            quantity,
          },
          signedAccessToken,
          store_id
        );

        if (!res || !res.id) return;
        methods.setValue('id', res?.id);

        //SEND REQUEST TO DISCOUNT THE PRODUCT PRICE
        if (isDiscounted && discount_amount) {
          await addDiscountToProduct(
            res?.id,
            {
              discount_percentage: discount_amount, //since it is suppose to be in percentage
              product_id: res?.id,
              starts_date: discount_starts_at || undefined,
              ends_date: discount_ends_at || undefined,
            },
            signedAccessToken
          );
        }
        await invalidateProductList();
        presentIonToast('This product has been saved as a draft');
      } catch (error: any) {
        console.error(error);
        throw error;
      } finally {
        setBusy(false);
      }
    },
  });

  const setProductImage = useMutation({
    mutationFn: async () => {
      try {
        setBusy(true, 'Attaching images to product...');
        if (!signedAccessToken) return;
        const { id, product_images } = methods.getValues();

        const productImages = product_images.map((file, idx) => ({
          file,
          is_default: idx === 0,
        }));

        if (!id || !store_id) return;
        await addProductImage({
          id,
          files: productImages,
          authToken: signedAccessToken,
        });
        await updateProduct(
          id,
          { save_as: 'published' },
          signedAccessToken,
          store_id
        );
        presentIonToast('Product successfully published');
        await invalidateProductList();
        handleNext();
      } catch (error: any) {
        console.error(error.message);
        presentIonToast(error.message, 'error');
        throw error;
      } finally {
        setBusy(false);
      }
    },
  });

  //HANDLES SUBMISSION OF CURRENT STEP
  const handleStepOneSubmission = async () => {
    const isValid = await validateProductIfo();
    if (!isValid) return;
    try {
      createProduct.mutateAsync().then(() => {
        handleNext(); // Move to next step only after successful mutation
      });
    } catch (error: any) {
      presentIonToast(error.message, 'error');
      // Error is already handled in mutation error handler
      console.error('Product creation failed:', error);
    }
  };

  const handleStepTwoSubmission = async () => {
    const isValid = await methods.trigger(['product_images', 'id']);
    if (!isValid) return;

    try {
      await setProductImage.mutateAsync();
    } catch (error) {
      // Error is already handled in mutation error handler
      console.error('Image upload failed:', error);
    }
  };

  const handleSaveToDraft = async () => {
    const isValid = await validateProductIfo();
    if (!isValid) return;
    try {
      setBusy(true);
      await createProduct.mutateAsync();
      presentIonToast('Product saved to draft');
      closeDialog(); // Move to next step only after successful mutation
    } catch (error) {
      // Error is already handled in mutation error handler
      console.error('Product creation failed:', error);
    } finally {
      setBusy(false);
    }
  };

  return {
    methods,
    handleSubmission,
    handleStepOneSubmission,
    handleStepTwoSubmission,
    handleNext,
    handleSaveToDraft,
    currentStep: step,
  };
};
